/**
 * Models offered in the assistant's picker.
 *
 * A curated allow-list, deliberately not a live mirror of the providers' /v1/models:
 * OpenAI returns 28 gpt/o-series entries (codex, search, nano, dated snapshots) that
 * are wrong or wasteful here, and an unfiltered list invites picking something that
 * costs a fortune. Anthropic's list is clean but still broader than we want.
 *
 * Every id below was verified present on Maven's accounts. Note gpt-5.3 does NOT
 * exist — a plausible-looking id is not a real one.
 */

export type ModelProvider = 'openai' | 'anthropic';

export type AssistantModel = {
  id: string;
  label: string;
  provider: ModelProvider;
  /** Shown beside the label so the cost of a choice is visible at the point of choosing. */
  note?: string;
  /** In the short list shown by default. */
  curated: boolean;
  /** Why it is not in the short list. Required for anything hidden. */
  hiddenReason?: string;
};

/**
 * Everything on Maven's accounts that makes sense for this assistant, verified against
 * both providers' /v1/models. `curated: false` entries are hidden behind "show all" —
 * each says why, so the checkbox reveals reasoning rather than a mystery pile.
 *
 * Deliberately absent: embeddings, codex, search-api and nano variants (wrong shape for
 * a grounded chat), and dated snapshots of models already listed by their rolling id.
 */
export const ALL_MODELS: AssistantModel[] = [
  // ── OpenAI ──
  { id: 'gpt-5.2', label: 'GPT-5.2', provider: 'openai', note: 'current default', curated: true },
  { id: 'gpt-5.4', label: 'GPT-5.4', provider: 'openai', curated: true },
  { id: 'gpt-5.2-pro', label: 'GPT-5.2 Pro', provider: 'openai', curated: false,
    hiddenReason: 'materially more expensive per call' },
  { id: 'gpt-5-pro', label: 'GPT-5 Pro', provider: 'openai', curated: false,
    hiddenReason: 'superseded by 5.2, and priced at the pro tier' },
  { id: 'gpt-5', label: 'GPT-5', provider: 'openai', curated: false,
    hiddenReason: 'previous generation' },
  { id: 'gpt-5-mini', label: 'GPT-5 mini', provider: 'openai', curated: false,
    hiddenReason: 'previous generation, small tier' },
  { id: 'gpt-4.1', label: 'GPT-4.1', provider: 'openai', curated: false,
    hiddenReason: 'older generation, kept for baseline comparisons' },
  { id: 'gpt-4o', label: 'GPT-4o', provider: 'openai', curated: false,
    hiddenReason: 'older generation, kept for baseline comparisons' },

  // ── Anthropic ──
  { id: 'claude-opus-5', label: 'Claude Opus 5', provider: 'anthropic', note: 'most capable', curated: true },
  { id: 'claude-sonnet-5', label: 'Claude Sonnet 5', provider: 'anthropic', note: 'faster, cheaper', curated: true },
  { id: 'claude-fable-5-1', label: 'Claude Fable 5.1', provider: 'anthropic', curated: false,
    hiddenReason: 'priced above the Opus tier' },
  { id: 'claude-opus-4-8', label: 'Claude Opus 4.8', provider: 'anthropic', curated: false,
    hiddenReason: 'previous generation' },
  { id: 'claude-opus-4-7', label: 'Claude Opus 4.7', provider: 'anthropic', curated: false,
    hiddenReason: 'previous generation' },
  { id: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6', provider: 'anthropic', curated: false,
    hiddenReason: 'previous generation' },
  { id: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5', provider: 'anthropic', curated: false,
    hiddenReason: 'small tier; useful as a cheap baseline' },
];

/** The short list, shown unless "show all models" is ticked. */
export const ASSISTANT_MODELS: AssistantModel[] = ALL_MODELS.filter((m) => m.curated);

/** Must match ai-looker-upper's ASSISTANT_MODEL, or the picker changes behaviour by default. */
export const DEFAULT_MODEL_ID = 'gpt-5.2';

/**
 * The second column on the comparison workbench. An Anthropic model, so the page
 * opens on an actual cross-provider comparison rather than GPT against itself.
 */
export const SECOND_MODEL_ID = 'claude-opus-5';

export const PROVIDER_LABELS: Record<ModelProvider, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
};

/** Resolves hidden models too, so a choice stays valid if "show all" is unticked. */
export function getModel(id: string): AssistantModel | undefined {
  return ALL_MODELS.find((m) => m.id === id);
}

/** Grouped for an optgroup-style menu, in catalogue order. */
export function modelsByProvider(
  { showAll = false }: { showAll?: boolean } = {},
): { provider: ModelProvider; models: AssistantModel[] }[] {
  const source = showAll ? ALL_MODELS : ASSISTANT_MODELS;
  const order: ModelProvider[] = [];
  for (const m of source) if (!order.includes(m.provider)) order.push(m.provider);
  return order.map((provider) => ({
    provider,
    models: source.filter((m) => m.provider === provider),
  }));
}
